import type { CallbackInterface } from 'recoil'

import { logAppError } from 'Helpers/logger'

import type { RecipientGroupsState } from 'Pages/RecipientsList/model'

import { fetchPagePreferences } from 'Pages/RecipientsList/recoil/services/filters'
import {
  addRecipientToGroup,
  addRecipientToNewGroup,
  fetchAllRecipientGroups,
  fetchRecipientGroups,
  fetchRecipientsGroups,
  removeRecipientFromGroup,
} from 'Pages/RecipientsList/recoil/services/groups'
import {
  recipientAllGroupsState,
  recipientGroupsState,
} from 'Pages/RecipientsList/recoil/state/recipient'
import { resultsState } from 'Pages/RecipientsList/recoil/state/results'

import { recipientsGroupsState } from 'Pages/RecipientsList/recoil/state/groups'

export const initGroupsCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (idRecipient: number) => {
    set(recipientGroupsState, (prev) => ({ ...prev, loading: true }))

    try {
      const [{ groups: groupsPrefs }, allGroups] = await Promise.all([
        fetchPagePreferences(),
        fetchAllRecipientGroups(idRecipient),
      ])

      const groups = await fetchRecipientGroups(idRecipient, {
        pageNumber: 1,
        pageSize: 10,
        orderBy: groupsPrefs.orderBy,
        orderDescending: groupsPrefs.orderDescending,
      })

      set<RecipientGroupsState>(recipientGroupsState, (prev) => ({
        ...prev,
        ...groups,
        status: 'ready',
      }))

      set(recipientAllGroupsState, allGroups)
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'initGroupsCallback',
        appError: error,
      })

      set<RecipientGroupsState>(recipientGroupsState, (prev) => ({
        ...prev,
        status: 'error',
      }))
    }

    set(recipientGroupsState, (prev) => ({ ...prev, loading: false }))
  }

export const resetGroupsCallback =
  ({ reset }: CallbackInterface) =>
  async () => {
    reset(recipientGroupsState)
    reset(recipientAllGroupsState)
  }

export const fetchGroupsCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (
    idRecipient: number,
    {
      pageNumber,
      pageSize,
      orderBy,
    }: {
      pageNumber?: number
      pageSize?: number
      orderBy?: 'IdGroup' | 'GroupName' | 'AssignmentDate'
    },
  ) => {
    const prevPagination =
      await snapshot.getPromise<RecipientGroupsState>(recipientGroupsState)

    set(recipientGroupsState, (prev) => ({ ...prev, loading: true }))

    try {
      const newPagination = {
        pageNumber:
          pageNumber !== undefined ? pageNumber : prevPagination.pageNumber,
        pageSize: pageSize !== undefined ? pageSize : prevPagination.pageSize,
        orderBy: orderBy !== undefined ? orderBy : prevPagination.orderBy,
        orderDescending:
          orderBy !== undefined && orderBy === prevPagination.orderBy
            ? !prevPagination.orderDescending
            : prevPagination.orderDescending,
      }

      const groups = await fetchRecipientGroups(idRecipient, newPagination)

      set<RecipientGroupsState>(recipientGroupsState, (prev) => ({
        ...prev,
        ...groups,
        orderBy: newPagination.orderBy,
        orderDescending: newPagination.orderDescending,
      }))
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'fetchGroupsCallback',
        appError: error,
      })

      set<RecipientGroupsState>(recipientGroupsState, (prev) => ({
        ...prev,
        status: 'error',
      }))
    }

    set(recipientGroupsState, (prev) => ({ ...prev, loading: false }))
  }

export const addRecipientToGroupCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (idRecipient: number, idGroup: number) => {
    const { pageNumber, pageSize, orderBy, orderDescending } =
      await snapshot.getPromise(recipientGroupsState)

    try {
      const added = await addRecipientToGroup(idRecipient, idGroup)

      if (added) {
        set(recipientGroupsState, (prev) => ({ ...prev, loading: true }))

        const groups = await fetchRecipientGroups(idRecipient, {
          pageNumber,
          pageSize,
          orderBy,
          orderDescending,
        })

        set<RecipientGroupsState>(recipientGroupsState, (prev) => ({
          ...prev,
          ...groups,
          loading: false,
        }))

        set(recipientAllGroupsState, (prev) => [...prev, idGroup])
      }
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'addRecipientToGroupCallback',
        appError: error,
      })

      set<RecipientGroupsState>(recipientGroupsState, (prev) => ({
        ...prev,
        status: 'error',
      }))
    }
  }

export const addRecipientToNewGroupCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (
    idRecipient: number,
    groupName: string,
  ): Promise<number | undefined> => {
    const { pageNumber, pageSize, orderBy, orderDescending } =
      await snapshot.getPromise(recipientGroupsState)

    set(recipientGroupsState, (prev) => ({ ...prev, loading: true }))

    try {
      const newGroupId = await addRecipientToNewGroup(idRecipient, groupName)

      const groups = await fetchRecipientGroups(idRecipient, {
        pageNumber,
        pageSize,
        orderBy,
        orderDescending,
      })

      set<RecipientGroupsState>(recipientGroupsState, (prev) => ({
        ...prev,
        ...groups,
        loading: false,
      }))

      set(recipientAllGroupsState, (prev) => [...prev, newGroupId])

      return newGroupId
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'addRecipientToNewGroupCallback',
        appError: error,
      })

      set<RecipientGroupsState>(recipientGroupsState, (prev) => ({
        ...prev,
        status: 'error',
      }))
    }

    set(recipientGroupsState, (prev) => ({ ...prev, loading: false }))
  }

export const removeRecipientFromGroupCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (idRecipient: number, idGroup: number) => {
    const { pageNumber, pageSize, orderBy, orderDescending } =
      await snapshot.getPromise(recipientGroupsState)

    set(recipientGroupsState, (prev) => ({ ...prev, loading: true }))

    try {
      await removeRecipientFromGroup(idRecipient, idGroup)

      const groups = await fetchRecipientGroups(idRecipient, {
        pageNumber,
        pageSize,
        orderBy,
        orderDescending,
      })

      set<RecipientGroupsState>(recipientGroupsState, (prev) => ({
        ...prev,
        ...groups,
      }))

      set(recipientAllGroupsState, (prev) => prev.filter((g) => g !== idGroup))
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'removeRecipientFromGroupCallback',
        appError: error,
      })

      set<RecipientGroupsState>(recipientGroupsState, (prev) => ({
        ...prev,
        status: 'error',
      }))
    }

    set(recipientGroupsState, (prev) => ({ ...prev, loading: false }))
  }

export const fetchRecipientsGroupsCallback =
  ({ snapshot, set }: CallbackInterface) =>
  async () => {
    const items = await snapshot.getPromise(resultsState)

    set(recipientsGroupsState, {
      status: 'loading',
      items: [],
    })

    try {
      const results = await fetchRecipientsGroups(items)

      set(recipientsGroupsState, {
        status: 'ready',
        items: results,
      })
    } catch (error) {
      logAppError({
        Component: 'RecipientsList',
        Method: 'fetchRecipientsGroupsCallback',
        appError: error,
      })

      set(recipientsGroupsState, {
        status: 'error',
        items: [],
      })
    }
  }
